import { collection, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc, onSnapshot, query, where, getDocs,
  DocumentReference, DocumentData, CollectionReference, DocumentSnapshot, QuerySnapshot, Firestore, QueryDocumentSnapshot } from "firebase/firestore";
import { useAuth } from "../componentes/Context";
import { useAuditoria } from "./useAuditoria";

// Definición de tipos para las opciones
interface OnSnapshotOptions {
  query?: any;
  filtros?: string | Record<string, any>;
  onError?: (error: Error) => void;
}

/**
 * Hook para operaciones de Firestore con auditoría integrada
 * 
 * Este hook envuelve las operaciones comunes de Firestore y registra automáticamente
 * las acciones en la auditoría para tener un registro completo de todas las operaciones
 */
export const useFirestoreWithAudit = () => {
  const { auditar } = useAuditoria();
  const auth = useAuth();
  const sucursal = auth.sucursal;
  const currentUser = auth.currentUser;
  
  /**
   * Obtiene un documento con registro de auditoría
   * (Ya no audita consultas según la nueva configuración)
   */
  const getDocWithAudit = async (
    firestore: Firestore,
    collectionPath: string,
    docId: string,
    sectionName: string
  ): Promise<DocumentSnapshot<DocumentData>> => {
    try {
      const docRef = doc(firestore, collectionPath, docId);
      const result = await getDoc(docRef);
      
      // Ya no registramos consultas en la auditoría
      // La función auditar ahora filtra automáticamente las consultas
      
      return result;
    } catch (error) {
      console.error("Error al obtener documento:", error);
      throw error;
    }
  };
  
  /**
   * Agrega un documento con registro de auditoría
   */
  const addDocWithAudit = async (
    firestore: Firestore,
    collectionPath: string,
    data: DocumentData,
    sectionName: string
  ): Promise<DocumentReference<DocumentData>> => {
    try {
      const collectionRef = collection(firestore, collectionPath);
      const result = await addDoc(collectionRef, data);
      
      // Registrar en auditoría
      if (currentUser?.uid && sucursal) {
        // Crear una copia segura de los datos para la auditoría (sin contraseñas)
        const safeData = { ...data };
        if ('password' in safeData) safeData.password = '[OCULTO]';
        if ('contraseña' in safeData) safeData.contraseña = '[OCULTO]';
        
        auditar('crear', sectionName, {
          coleccion: collectionPath,
          documento: result.id,
          datos: safeData
        }).catch(error => {
          console.error(`Error al registrar auditoría de addDoc [${sectionName}]:`, error);
        });
      }
      
      return result;
    } catch (error) {
      console.error("Error al agregar documento:", error);
      throw error;
    }
  };
  
  /**
   * Actualiza un documento con registro de auditoría
   */
  const updateDocWithAudit = async (
    firestore: Firestore,
    collectionPath: string,
    docId: string,
    data: DocumentData,
    sectionName: string
  ): Promise<boolean> => {
    try {
      const docRef = doc(firestore, collectionPath, docId);
      await updateDoc(docRef, data);
      
      // Registrar en auditoría
      if (currentUser?.uid && sucursal) {
        // Crear una copia segura de los datos para la auditoría (sin contraseñas)
        const safeData = { ...data };
        if ('password' in safeData) safeData.password = '[OCULTO]';
        if ('contraseña' in safeData) safeData.contraseña = '[OCULTO]';
        if ('nuevaPassword' in safeData) safeData.nuevaPassword = '[OCULTO]';
        
        auditar('editar', sectionName, {
          coleccion: collectionPath,
          documento: docId,
          datos: safeData
        }).catch(error => {
          console.error(`Error al registrar auditoría de updateDoc [${sectionName}]:`, error);
        });
      }
      
      return true;
    } catch (error) {
      console.error("Error al actualizar documento:", error);
      throw error;
    }
  };
  
  /**
   * Elimina un documento con registro de auditoría
   */
  const deleteDocWithAudit = async (
    firestore: Firestore,
    collectionPath: string,
    docId: string,
    sectionName: string
  ): Promise<boolean> => {
    try {
      const docRef = doc(firestore, collectionPath, docId);
      await deleteDoc(docRef);
      
      // Registrar en auditoría
      if (currentUser?.uid && sucursal) {
        auditar('eliminar', sectionName, {
          coleccion: collectionPath,
          documento: docId
        }).catch(error => {
          console.error(`Error al registrar auditoría de deleteDoc [${sectionName}]:`, error);
        });
      }
      
      return true;
    } catch (error) {
      console.error("Error al eliminar documento:", error);
      throw error;
    }
  };
  
  /**
   * Configura un onSnapshot con registro de auditoría inicial
   * (Ya no audita consultas según la nueva configuración)
   */
  const onSnapshotWithAudit = (
    firestore: Firestore,
    collectionPath: string,
    sectionName: string,
    callback: (snapshot: QuerySnapshot<DocumentData>) => void,
    options: OnSnapshotOptions = {}
  ): (() => void) => {
    // Ya no registramos consultas en la auditoría
    // La función auditar ahora filtra automáticamente las consultas
    
    // Configurar el onSnapshot normal
    let queryRef;
    if (options.query) {
      queryRef = options.query;
    } else {
      queryRef = collection(firestore, collectionPath);
    }
    
    return onSnapshot(
      queryRef, 
      (snapshot: QuerySnapshot<DocumentData>) => {
        callback(snapshot);
      },
      (error: Error) => {
        console.error(`Error en onSnapshot [${sectionName}]:`, error);
        if (options.onError) options.onError(error);
      }
    );
  };
  
  return {
    getDocWithAudit,
    addDocWithAudit,
    updateDocWithAudit,
    deleteDocWithAudit,
    onSnapshotWithAudit
  };
};
